import { TaskStatus } from 'owa-todo-types';
import { OwaDate } from 'owa-datetime';

export enum OrderByValue {
    CompletedDateTime,
    OrderDateTime,
    CommittedOrder,
}

export enum ExpandValue {
    LinkedEntities = 'LinkedEntities',
}

export enum GetTasksRequestType {
    MyDay,
    DueDate,
    FolderId,
}

interface CustomQueryOptions {
    /**
     * Maximum number of tasks to fetch
     *
     * @type {number}
     */
    maxPageSize?: number;
}

export type EqualityOperator = 'eq' | 'ne';

export interface TaskStatusFilter {
    /**
     * Task status to filter by
     *
     * @type {TaskStatus}
     */
    status: TaskStatus;

    /**
     * Equality operator used to indicate the nature of
     * the task status filter
     *
     * @type {EqualityOperator}
     */
    equalityOperator: EqualityOperator;
}

export interface GetTasksRequestFilter {
    taskStatusFilters: TaskStatusFilter[];
}

/**
 * Interface for the shape of requests to get tasks. For more information, see the
 * Todo Api Documentation: https://alexanderplatz.visualstudio.com/To-Do/_git/to-do-api-documentation?anchor=Conclusion
 */
export interface GetTasksRequestParamsBase {
    /**
     * Used to build the filter system query option portion of tasks request.($filter)
     *
     * @type {taskStatusFilter: TaskStatusFilter}
     */
    filters: GetTasksRequestFilter;

    /**
     * Used to build the Orderby System Query Option ($orderby)
     *
     * @type {OrderByValue}
     */
    orderBy: OrderByValue;

    /**
     * Used to build the Expand System Query Option ($expand)
     *
     * @type {ExpandValue}
     */
    expand?: ExpandValue;

    /**
     * Used to build the Custom query options that are defined by the endpoint in the form of option=value
     *
     * @type {CustomQueryOptions}
     */
    customQueryOptions: CustomQueryOptions;
}

/**
 * Todo MyDay Endpoint documentation: https://alexanderplatz.visualstudio.com/To-Do/_git/to-do-api-documentation?anchor=Conclusion&path=%2FTeamDocuments%2FMyDay.md&_a=preview
 */
export interface GetMyDayTasksRequestParams extends GetTasksRequestParamsBase {
    type: GetTasksRequestType.MyDay;

    /**
     * Used to build the Custom query options for the MyDay request. Date here indicates the Date
     * which we are requesting the tasks for.
     *
     * @type {CustomQueryOptions & { date: OwaDate;}
     */
    customQueryOptions: CustomQueryOptions & {
        date: OwaDate;
    };
}

/**
 * Todo Due Date Endpoint documentation: https://alexanderplatz.visualstudio.com/To-Do/_git/to-do-api-documentation?anchor=Conclusion&path=%2FTeamDocuments%2FMyDay.md&_a=preview
 */
export interface GetDueDateTasksRequestParams extends GetTasksRequestParamsBase {
    type: GetTasksRequestType.DueDate;

    /**
     * Used to build the Custom query options for the DueDate request. StartDate and endDate here indicate the date range
     * which we are requesting the tasks for.
     *
     * @type {CustomQueryOptions & { date: OwaDate;}
     */
    customQueryOptions: CustomQueryOptions & {
        startDate: OwaDate;
        endDate: OwaDate;
    };
}

export interface GetFolderTasksRequestParams extends GetTasksRequestParamsBase {
    type: GetTasksRequestType.FolderId;

    /**
     * FolderId to request tasks for
     *
     * @type {string}
     */
    folderId?: string;
}

export type GetTasksRequestParams =
    | GetMyDayTasksRequestParams
    | GetFolderTasksRequestParams
    | GetDueDateTasksRequestParams;
